import { useWallet } from '@bifrost-platform/bifront-sdk-react-wallet';
import { Trans, useTranslation } from 'next-i18next';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { PATH_TERMS } from '@/configs/path';
import { REGEX_EMAIL } from '@/configs/regex';
import { SNACKBAR_PROPS_AUTO_CLOSE } from '@/configs/toastContainerProps';
import useDeviceSize from '@/hooks/mobile/useDeviceSize';
import useEnv from '@/hooks/useEnv';
import useFirestore from '@/hooks/useFirestore';
import useInputValue from '@/hooks/useInputValue';
import useOpen from '@/hooks/useOpen';
import { openErrorSnackbar, openSuccessSnackbar } from '@/libs/snackbar';
import { handleEmailInputChange } from '@/utils/input';
import HeaderDialog from './HeaderDialog';
import { Center, ColumnFlex } from '../Flex';
import { IconCrown } from '../icons';
import ActionButton from '../input/ActionButton';
import Checkbox, { CheckboxLabelLink } from '../input/Checkbox';
import Input from '../input/Input';
import Dialog, {
  DEFAULT_CLOSE_SIZE,
  Props as DialogProps,
} from '../surface/Dialog';
import StyledText from '../text/StyledText';

export interface Props extends DialogProps {}

const Label = styled.div`
  & > span {
    color: ${({ theme }) => theme.colors.active};
  }
`;

export default function DialogWaitingListRequest({
  isShow,
  onCloseClick: handleCloseClick,
  ...props
}: Props) {
  // device size
  const deviceSize = useDeviceSize();

  // translate
  const { t: commonT } = useTranslation('common');
  const { t } = useTranslation('dialog');

  // firestore
  const { postEmail } = useFirestore({});

  // env
  const { isTestnet } = useEnv();

  // wallet
  const { account } = useWallet();

  // open
  const {
    value: isLoading,
    handleOpen: handleLoadingStart,
    handleClose: handleLoadingEnd,
  } = useOpen();
  const { value: isChecked, handleSwitch: handleCheckboxClick } = useOpen();

  // input value
  const {
    value: inputEmail,
    setValue: setInputEmail,
    handleChange: handleInputEmailChange,
  } = useInputValue();

  // memo
  const isValidEmail = useMemo(
    () => REGEX_EMAIL.test(inputEmail),
    [inputEmail]
  );
  const isDisabledButton = useMemo(
    () => !(isChecked && isValidEmail && !isLoading),
    [isChecked, isValidEmail, isLoading]
  );

  // callback
  const register = useCallback(
    async (email: string) => {
      handleLoadingStart();

      try {
        await postEmail(email);

        setInputEmail('');

        openSuccessSnackbar({
          title: t('waitingList.success.title'),
          message: t('waitingList.success.description'),
          isSingleWord: true,
        });
      } catch (error) {
        openErrorSnackbar({
          t: commonT,
          error,
          message: commonT('errorMessage.failedRegister'),
          ...SNACKBAR_PROPS_AUTO_CLOSE,
          isTestnet,
          account,
        });
      }

      handleLoadingEnd();
    },
    [
      handleLoadingStart,
      handleLoadingEnd,
      postEmail,
      setInputEmail,
      t,
      commonT,
      isTestnet,
      account,
    ]
  );
  const handleClick = useCallback(() => {
    register(inputEmail);
  }, [register, inputEmail]);

  return (
    <Dialog
      isShow={isShow}
      isShowOverflow
      onCloseClick={handleCloseClick}
      minWidth={343}
      padding="xs"
      dimPadding={{
        x: 10,
        y: deviceSize === 'mobile' ? 380 : deviceSize === 'tablet' ? 450 : 530,
      }}
      rounded="md"
      bg="background"
      closeNodePosition={{
        top: -(DEFAULT_CLOSE_SIZE + 10),
        bottom: 'unset',
        left: 'unset',
        right: 'zero',
      }}
      {...props}
    >
      <ColumnFlex $gap="md">
        <HeaderDialog>
          <Center $gap="xxxs">
            <IconCrown color="active" />
            <StyledText $fontSize="md" $fontWeight="bold">
              BTCFi Pioneer Program
            </StyledText>
          </Center>
        </HeaderDialog>
        <ColumnFlex $gap="xs">
          <StyledText
            $fontSize="xs"
            $color="secondary"
            $textAlign="center"
            $whiteSpace="pre-wrap"
          >
            {t('waitingList.description')}
          </StyledText>
          <Input
            height={40}
            fontSize="sm"
            textAlign="center"
            borderWidth={0}
            rounded="full"
            placeholder="email@address.com"
            type="email"
            maxLength={256}
            value={inputEmail}
            onChange={handleEmailInputChange(handleInputEmailChange)}
          />
          <Center>
            <Checkbox
              value={isChecked ? 'true' : ''}
              onClick={handleCheckboxClick}
              label={
                <StyledText
                  $fontSize="xs"
                  $color="secondary"
                  $textAlign="center"
                  $whiteSpace="pre-wrap"
                >
                  <Label>
                    <Trans
                      i18nKey="dialog:waitingList.terms"
                      components={[
                        <CheckboxLabelLink href={PATH_TERMS} key={0} />,
                      ]}
                    />
                  </Label>
                </StyledText>
              }
            />
          </Center>
        </ColumnFlex>
        <ActionButton
          isDisabled={isDisabledButton}
          $height={48}
          onClick={handleClick}
        >
          {t('waitingList.register')}
        </ActionButton>
      </ColumnFlex>
    </Dialog>
  );
}
